import type { ReactNode } from "react";
import { Share2 } from "lucide-react";

import type { WaitlistPosition } from "../../lib/waitlist-api";
import { Button } from "../ui/button";

/**
 * Waitlist position card (M11-C2, plan 317 — design board 166). STANDALONE
 * surface: it sits beside, and never restyles, PassportPublishBlock's states.
 * Pure/presentational — data comes from the caller.
 *
 * THREE states, keyed on reveal-threshold + accrued referral credits:
 *   - Unlock (count < reveal_threshold): "Sill isn't live in {area} yet — {count}
 *     interested. Refer to bring it here." No rank (cold-start framing).
 *   - Waiting (revealed, 0 credits): rank + "opens in {area} soon" + refer CTA.
 *   - Moved up (revealed, >0 credits): old→new rank + "you jumped N spots".
 *
 * CONFIG-DERIVED COPY (drift delta 5): "~N spots" and "jumped M spots" derive
 * from `per_referral_boost` — the SAME config the backend reorders on.
 *
 * REFER CTA is OPT-IN via `onShare`: the "refer friends to move up" sub-box (and
 * the moved-up "Share again" button) render ONLY when `onShare` is provided.
 * Both live containers (`WaitlistPositionCardContainer`, `BrowseWaitlistState`)
 * now pass it (plan 317 task 1483, absorbed by the referral discovery
 * surfaces) and open the shared `ReferralShareSheet` — the M11-C5
 * `consumer_referral` `?referral_token=` link, never C1's `/?ref=`
 * `referralInviteURL` (which collided with the agent-invite lane → agent-preview
 * 404 and never attributed). Fixture/preview renders may still omit it.
 */
export interface WaitlistPositionCardProps {
  waitlist: WaitlistPosition;
  // Opt-in refer CTA. Omitted in C2 (plan 317) — see the component doc; the
  // consumer share funnel is plan 319 (#555). When absent, no share UI renders.
  onShare?: () => void;
  onLeave?: () => void;
  sharing?: boolean;
  /**
   * "start" (default) keeps the passport-board-166 left alignment; "center"
   * centers the card body + controls for the standalone browse-gate placement.
   */
  align?: "start" | "center";
}

export function WaitlistPositionCard({
  waitlist,
  onShare,
  onLeave,
  sharing = false,
  align = "start",
}: WaitlistPositionCardProps) {
  const boost = waitlist.per_referral_boost;
  const area = waitlist.area_label;
  const centered = align === "center";
  const colAlign = centered ? " items-center" : "";

  let body: ReactNode;
  let state: string;
  if (!waitlist.revealed) {
    state = "unlock";
    body = (
      <div className={`flex flex-col gap-4${colAlign}`}>
        <div className={`flex flex-col gap-1${colAlign}`}>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Sill isn't live in {area} yet
          </p>
          <p
            className="text-5xl font-extrabold tabular-nums text-primary"
            data-testid="waitlist-count"
          >
            {waitlist.count}
          </p>
          <p className="text-sm text-muted-foreground">
            {waitlist.count === 1 ? "buyer interested here" : "buyers interested here"}
          </p>
        </div>
        {onShare && (
          <div className="rounded-xl bg-muted/50 px-4 py-3">
            <p className="text-sm font-semibold text-foreground">
              Help bring Sill to {area}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              The more buyers who join, the sooner we launch here. Refer friends
              to move {area} up our list.
            </p>
            <div className="mt-3">
              <ShareButton onShare={onShare} sharing={sharing} label="Share your invite link" />
            </div>
          </div>
        )}
      </div>
    );
  } else if (waitlist.referral_credits > 0) {
    state = "moved-up";
    const jumped = waitlist.referral_credits * boost;
    const prior = (waitlist.position ?? 0) + jumped;
    const friends = waitlist.referral_credits;
    body = (
      <div className={`flex flex-col gap-3${colAlign}`}>
        <p className="text-sm font-medium text-muted-foreground">
          {friends === 1 ? "1 friend joined" : `${friends} friends joined`}
        </p>
        <div className={`flex items-baseline gap-3${centered ? " justify-center" : ""}`}>
          <span className="text-3xl font-extrabold tabular-nums text-muted-foreground line-through">
            #{prior}
          </span>
          <span aria-hidden className="text-muted-foreground">
            →
          </span>
          <span
            className="text-5xl font-extrabold tabular-nums text-primary"
            data-testid="waitlist-position-number"
          >
            #{waitlist.position}
          </span>
        </div>
        <p className="text-base font-medium text-foreground">
          You jumped {jumped} spots. Keep going.
        </p>
        {onShare && (
          <ShareButton onShare={onShare} sharing={sharing} label="Share again" />
        )}
      </div>
    );
  } else {
    state = "waiting";
    body = (
      <div className={`flex flex-col gap-4${colAlign}`}>
        <div className={`flex flex-col gap-1${colAlign}`}>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            The marketplace opens in {area} soon
          </p>
          <p
            className="text-5xl font-extrabold tabular-nums text-primary"
            data-testid="waitlist-position-number"
          >
            #{waitlist.position}
          </p>
          <p className="text-sm text-muted-foreground">your spot in line</p>
          <p className="text-sm text-muted-foreground">
            {waitlist.ahead} ahead · {waitlist.behind} behind you
          </p>
        </div>
        {onShare && (
          <div className="rounded-xl bg-muted/50 px-4 py-3">
            <p className="text-sm font-semibold text-foreground">
              Refer friends to move up
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Each friend who joins moves you up ~{boost} spots.
            </p>
            <div className="mt-3">
              <ShareButton
                onShare={onShare}
                sharing={sharing}
                label="Share your invite link"
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <section
      className={`marketplace-surface px-5 py-5${centered ? " text-center" : ""}`}
      data-testid="waitlist-position-card"
      data-state={state}
    >
      {body}
      {onLeave && (
        <button
          type="button"
          onClick={onLeave}
          className="mt-4 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
          data-testid="waitlist-leave-btn"
        >
          Leave the waitlist
        </button>
      )}
    </section>
  );
}

function ShareButton({
  onShare,
  sharing,
  label,
}: {
  onShare?: () => void;
  sharing?: boolean;
  label: string;
}) {
  return (
    <Button
      type="button"
      onClick={onShare}
      disabled={sharing || !onShare}
      className="w-fit rounded-full"
      data-testid="waitlist-share-btn"
    >
      <Share2 className="mr-2 size-4" aria-hidden />
      {sharing ? "Sharing…" : label}
    </Button>
  );
}
